import { Fragment, useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, FloppyDisk, Eye, RocketLaunch, ArrowCounterClockwise, Lock, ClockCounterClockwise } from "@phosphor-icons/react";
import { CustomField } from "@/components/CustomField";
import { SectionCard, newSection, move } from "@/components/FormBuilderParts";
import { MEMBER_TYPE_LABELS } from "@/lib/constants";
import { toast } from "sonner";

function Preview({ open, onOpenChange, sections, typeLabel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fb-preview-dialog">
        <DialogHeader><DialogTitle>Preview: {typeLabel} form</DialogTitle><DialogDescription>This is how the draft will be laid out. Core fields keep their usual inputs.</DialogDescription></DialogHeader>
        <div className="space-y-5">
          {sections.filter((s) => s.fields.length).map((s) => (
            <div key={s.id} className="rounded-md border border-border p-4">
              <p className="font-display font-semibold mb-3">{s.title}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {s.fields.map((f) => f.core
                  ? <div key={f.key} className="space-y-1.5"><p className="text-sm font-medium">{f.label}</p><div className="h-9 rounded-md border border-dashed border-border bg-muted/40 text-xs text-muted-foreground flex items-center gap-1 px-3"><Lock size={12} />Standard input</div></div>
                  : <div key={f.key}><CustomField field={f} value={f.type === "checkboxes" ? [] : ""} onChange={() => {}} disabled />{f.show_if && <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">Shown only for some answers</p>}</div>)}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VersionHistory({ versions, onRestore, busy }) {
  const [pick, setPick] = useState(null);
  return (
    <Card className="mt-8 p-5 border-border" data-testid="fb-history">
      <p className="font-display text-lg font-semibold flex items-center gap-2"><ClockCounterClockwise size={18} />Change history</p>
      <p className="text-sm text-muted-foreground mt-1">Every published version. Restoring loads it as a draft so you can review it before publishing.</p>
      <div className="mt-4 space-y-3">
        {versions.map((v) => (
          <div key={v.version} className="rounded-md border border-border p-3" data-testid={`fb-version-${v.version}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-sm">Version {v.version}</span>
              <span className="text-xs text-muted-foreground">{new Date(v.published_at).toLocaleString()} · {v.published_by_name || v.published_by}</span>
              <Button size="sm" variant="outline" className="ml-auto gap-1.5" disabled={busy} onClick={() => setPick(v.version)} data-testid={`fb-version-restore-${v.version}`}><ArrowCounterClockwise size={14} />Restore as draft</Button>
            </div>
            <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5 space-y-0.5">{v.changes.slice(0, 12).map((c, i) => <li key={i}>{c}</li>)}{v.changes.length > 12 && <li>and {v.changes.length - 12} more</li>}</ul>
          </div>
        ))}
        <div className="rounded-md border border-dashed border-border p-3 flex items-center gap-2" data-testid="fb-version-0">
          <span className="text-sm">Original default form</span>
          <Button size="sm" variant="outline" className="ml-auto gap-1.5" disabled={busy} onClick={() => setPick(0)} data-testid="fb-version-restore-0"><ArrowCounterClockwise size={14} />Restore as draft</Button>
        </div>
      </div>
      <AlertDialog open={pick !== null} onOpenChange={(o) => !o && setPick(null)}>
        <AlertDialogContent data-testid="fb-restore-dialog">
          <AlertDialogHeader><AlertDialogTitle>Load {pick ? `version ${pick}` : "the original form"} as a draft?</AlertDialogTitle>
            <AlertDialogDescription>Your current unpublished draft will be replaced. Members won't see anything until you publish.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { onRestore(pick); setPick(null); }} data-testid="fb-restore-confirm">Load as draft</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function FormBuilder({ embedded }) {
  const Wrap = embedded ? Fragment : Layout;
  const [type, setType] = useState("public");
  const [meta, setMeta] = useState(null);
  const [sections, setSections] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [versions, setVersions] = useState([]);

  const load = useCallback(async () => {
    const [{ data }, hist] = await Promise.all([api.get(`/admin/forms/${type}`), api.get(`/admin/forms/${type}/versions`)]);
    setMeta(data); setSections(data.draft.sections); setDirty(false); setVersions(hist.data);
  }, [type]);
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const update = (next) => { setSections(next); setDirty(true); };
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveDraft = () => run(() => api.put(`/admin/forms/${type}/draft`, { sections }), "Draft saved");
  const publish = () => run(async () => { if (dirty) await api.put(`/admin/forms/${type}/draft`, { sections }); await api.post(`/admin/forms/${type}/publish`); }, "Form published. Members now see the new layout.");
  const discard = () => run(() => api.post(`/admin/forms/${type}/discard`), "Draft discarded");
  const moveFieldTo = (from, i, to) => {
    if (from === to) return;
    const field = sections[from].fields[i];
    update(sections.map((s, k) => (k === from ? { ...s, fields: s.fields.filter((_, j) => j !== i) } : k === to ? { ...s, fields: [...s.fields, field] } : s)));
  };
  const switchType = (t) => { if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return; setType(t); };
  const restore = (v) => run(() => api.post(`/admin/forms/${type}/versions/${v}/restore`), "Loaded as draft. Review it, then publish.");
  const flat = sections.flatMap((s) => s.fields);
  const sourcesFor = (key) => {
    const idx = flat.findIndex((f) => f.key === key);
    return flat.slice(0, idx).map((f) => ({ key: f.key, label: f.label,
      options: f.core ? meta?.core_conditions?.[f.key] : f.type === "yes_no" ? ["yes", "no"] : ["select", "radio", "checkboxes"].includes(f.type) ? f.options : null }))
      .filter((s) => s.options?.length);
  };

  return (
    <Wrap>
      <div className="animate-fade-up max-w-5xl" data-testid="form-builder-page">
        {!embedded && <><p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Organization</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-1">Form Builder</h1></>}
        <p className="text-muted-foreground mt-3 max-w-2xl">Edit the membership forms. Core fields can be renamed, moved and reordered but not removed. Changes reach members only after you publish.</p>
        <Tabs value={type} onValueChange={switchType} className="mt-6">
          <TabsList>{Object.entries(MEMBER_TYPE_LABELS).map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`fb-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Card className="mt-4 p-4 border-border flex flex-wrap items-center gap-3 sticky top-2 z-20 bg-card/95 backdrop-blur" data-testid="fb-toolbar">
          <span className="text-sm text-muted-foreground mr-auto" data-testid="fb-status">
            {meta ? `Published version ${meta.version}${meta.published_at ? ` · ${new Date(meta.published_at).toLocaleString()}` : " (default form)"}` : "Loading…"}
            {(dirty || meta?.has_unpublished) && <span className="ml-2 text-amber-700 dark:text-amber-400 font-medium" data-testid="fb-unpublished">{dirty ? "Unsaved changes" : "Draft not yet published"}</span>}
          </span>
          <Button variant="outline" onClick={() => setPreview(true)} className="gap-2" data-testid="fb-preview-button"><Eye size={16} />Preview</Button>
          <Button variant="outline" onClick={saveDraft} disabled={busy || !dirty} className="gap-2" data-testid="fb-save-draft"><FloppyDisk size={16} />Save draft</Button>
          {meta?.has_unpublished && <Button variant="ghost" onClick={discard} disabled={busy} className="gap-2" data-testid="fb-discard"><ArrowCounterClockwise size={16} />Discard draft</Button>}
          <Button onClick={() => setConfirm(true)} disabled={busy || (!dirty && !meta?.has_unpublished)} className="gap-2" data-testid="fb-publish"><RocketLaunch size={16} />Publish</Button>
        </Card>
        <div className="mt-5 space-y-4">
          {sections.map((s, i) => (
            <SectionCard key={s.id} section={s} index={i} count={sections.length} sections={sections}
              onChange={(ns) => update(sections.map((x, j) => (j === i ? ns : x)))}
              onMove={(d) => update(move(sections, i, d))}
              onRemove={() => update(sections.filter((_, j) => j !== i))}
              onFieldMoveTo={(fi, to) => moveFieldTo(i, fi, to)} sourcesFor={sourcesFor} />
          ))}
          <Button variant="outline" onClick={() => update([...sections, newSection()])} className="gap-2" data-testid="fb-add-section"><Plus size={16} />Add section</Button>
        </div>
        <VersionHistory versions={versions} onRestore={restore} busy={busy} />
      </div>
      <Preview open={preview} onOpenChange={setPreview} sections={sections} typeLabel={MEMBER_TYPE_LABELS[type]} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="fb-publish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish the {MEMBER_TYPE_LABELS[type]} form?</AlertDialogTitle>
            <AlertDialogDescription>Members will see the new layout straight away. Approved members will be asked to fill any new required fields when they next log in. Answers to removed fields are kept and still appear in exports.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fb-publish-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={publish} data-testid="fb-publish-confirm">Publish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Wrap>
  );
}
